import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import StatusBadge from '../components/ui/StatusBadge';

const DQ_RULES = [
  // Structural validators (DQ-1 to DQ-25)
  { code: 'DQ-1', name: 'Required Fields', description: 'All mandatory fields must be populated' },
  { code: 'DQ-2', name: 'Amount Positive', description: 'Transaction amounts must be positive' },
  { code: 'DQ-3', name: 'Date Not Future', description: 'Transaction dates must not be in the future' },
  { code: 'DQ-4', name: 'Date Reasonable', description: 'Dates must be within reasonable range (2020-2030)' },
  { code: 'DQ-5', name: 'Currency Valid', description: 'Currency must be a valid 3-letter ISO code' },
  { code: 'DQ-6', name: 'Entity Exists', description: 'Entity code must exist in entity registry' },
  { code: 'DQ-7', name: 'Vendor Exists', description: 'Vendor ID must exist in vendor master' },
  { code: 'DQ-8', name: 'Customer Exists', description: 'Customer ID must exist in customer master' },
  { code: 'DQ-9', name: 'GL Account Valid', description: 'GL account must exist in chart of accounts' },
  { code: 'DQ-10', name: 'Due Date Sequence', description: 'Due date must be on or after invoice date' },
  { code: 'DQ-11', name: 'Line Total Match', description: 'Header amount must equal sum of line amounts' },
  { code: 'DQ-12', name: 'Duplicate Number', description: 'Document number must be unique (no duplicates)' },
  { code: 'DQ-13', name: 'Email Format', description: 'Email addresses must be valid format' },
  { code: 'DQ-14', name: 'Phone Format', description: 'Phone numbers must contain valid characters' },
  { code: 'DQ-15', name: 'Payment Terms', description: 'Payment terms must match known patterns (NET30, etc.)' },
  { code: 'DQ-16', name: 'Credit Limit Cap', description: 'Credit limit must not exceed $100M' },
  { code: 'DQ-17', name: 'Quantity Positive', description: 'Line quantities must be positive' },
  { code: 'DQ-18', name: 'Unit Price Positive', description: 'Line unit prices must be positive' },
  { code: 'DQ-19', name: 'Contract Dates', description: 'Contract end date must be after start date' },
  { code: 'DQ-20', name: 'Expense Category', description: 'Expense category must be from approved list' },
  { code: 'DQ-21', name: 'Contract Type', description: 'Contract type must be valid (PURCHASE, SALES, SERVICE, etc.)' },
  { code: 'DQ-22', name: 'Recognition Method', description: 'ASC 606 recognition method must be valid (POINT_IN_TIME, RATABLE, etc.)' },
  { code: 'DQ-23', name: 'Tax ID Format', description: 'Tax ID must meet minimum length requirements' },
  { code: 'DQ-24', name: 'Country Code', description: 'Country code must be 2-letter ISO' },
  { code: 'DQ-25', name: 'Amount Precision', description: 'Monetary amounts must have max 2 decimal places' },
  // Business logic validators (DQ-26 to DQ-45)
  { code: 'DQ-26', name: 'JE Balanced', description: 'Journal entry total debits must equal total credits' },
  { code: 'DQ-27', name: 'No Same-Account DR/CR', description: 'Same GL account must not appear on both debit and credit side' },
  { code: 'DQ-28', name: 'Period Open', description: 'Posting period must not be HARD_CLOSED' },
  { code: 'DQ-29', name: 'Vendor Active', description: 'Vendor must be in ACTIVE status (not SUSPENDED/INACTIVE)' },
  { code: 'DQ-30', name: 'Customer Active', description: 'Customer must be in ACTIVE status' },
  { code: 'DQ-31', name: 'Credit Limit Check', description: 'Invoice/SO amount must not exceed customer credit limit + outstanding AR' },
  { code: 'DQ-32', name: 'Duplicate Payment', description: 'Detect duplicate: same vendor + amount within 7 days' },
  { code: 'DQ-33', name: 'PO Reference Valid', description: 'PO must exist and be in valid status for GR/invoice' },
  { code: 'DQ-34', name: 'GR Qty vs PO', description: 'Goods receipt quantity must not exceed PO remaining quantity' },
  { code: 'DQ-35', name: 'Vendor Duplicate', description: 'Fuzzy match to detect duplicate vendor names' },
  { code: 'DQ-36', name: 'Customer Duplicate', description: 'Fuzzy match to detect duplicate customer names' },
  { code: 'DQ-37', name: 'Manager Exists', description: 'Employee manager must exist and not be self-referencing' },
  { code: 'DQ-38', name: 'Contract Counterparty', description: 'Counterparty must exist (vendor for PURCHASE, customer for SALES)' },
  { code: 'DQ-39', name: 'Tax Jurisdiction', description: 'Tax jurisdiction must exist and be active' },
  { code: 'DQ-40', name: 'Tax Calc Correct', description: 'Tax amount must equal base amount x rate (within tolerance)' },
  { code: 'DQ-41', name: 'Bank Stmt Balanced', description: 'Opening balance + transactions must equal closing balance' },
  { code: 'DQ-42', name: 'Salary Reasonable', description: 'Employee salary must be within reasonable range ($1K-$5M)' },
  { code: 'DQ-43', name: 'Valuation Method', description: 'Inventory valuation method must be valid (FIFO, WEIGHTED_AVG, etc.)' },
  { code: 'DQ-44', name: 'Receipt Required', description: 'Expense lines above $25 must have receipt attachment' },
  { code: 'DQ-45', name: 'Approval Authority', description: 'Transaction amount must not exceed approver DOA limit' },
];

const columns: Column[] = [
  { key: 'code', label: 'Code', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)', fontFamily: 'monospace', fontSize: 11 }}>{v}</span> },
  { key: 'name', label: 'Rule', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
  { key: 'description', label: 'Description', render: (v: string) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span> },
];

export default function DataQualityWarnings() {
  return (
    <PageLayout title="Data Quality" subtitle="Validation rules enforced across all data ingestion and create endpoints">
      <KpiGrid
        columns={4}
        items={[
          { label: 'Total Rules', value: '25', color: 'var(--accent)' },
          { label: 'Status', value: 'ACTIVE', color: 'var(--positive)' },
          { label: 'Coverage', value: 'All Modules', color: 'var(--accent)' },
          { label: 'Enforcement', value: 'Create + Upload' },
        ]}
      />

      <div style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <StatusBadge status="ACTIVE" />
        <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
          Data quality validation is wired into all create endpoints and the data upload pipeline. Every record passes through these 25 rules before ingestion.
        </span>
      </div>

      <DataTable columns={columns} rows={DQ_RULES} rowKey="code" />
    </PageLayout>
  );
}
